import { useCallback, useEffect, useRef } from 'react';

// Pixels de défilement pour passer de pleine taille à réduite (et inversement).
const RANGE = 140;
// Près du haut, la barre ne peut pas être plus réduite que la distance au haut.
const TOP_ZONE = 40;
// Défilement arrêté depuis : la barre se cale sur l'état le plus proche.
const SETTLE_DELAY = 140;
// Part de l'écart rattrapée à chaque image : un défilement lent est suivi de
// près, un défilement rapide donne une réduction fluide au lieu d'un saut.
const EASE = 0.16;

const reducedMotion = () => typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Réduite = 78 %, un peu descendue, à peine plus transparente. */
const paint = (el, p) => {
  el.style.transform = p > 0 ? `translateY(${(8 * p).toFixed(2)}px) scale(${(1 - 0.22 * p).toFixed(4)})` : '';
  el.style.opacity = p > 0 ? String(1 - 0.08 * p) : '';
  el.dataset.compact = p >= 1 ? 'true' : p > 0 ? 'partial' : 'false';
};

/**
 * Barre du bas « à la Revolut » : elle rapetisse en suivant le défilement
 * vers le bas et regrandit en suivant le défilement vers le haut, puis se
 * cale sur pleine taille ou réduite quand le doigt est levé.
 *
 * Deux valeurs : la cible, que le défilement déplace, et la taille affichée,
 * qui la rattrape d'une fraction par image (aucun saut quand on défile vite).
 *
 * Les pages coach défilent dans des blocs (overflow-y-auto), pas dans la
 * fenêtre : on écoute le défilement de tout le document en capture, en
 * passif, une lecture par image, et on écrit le style directement sur
 * l'élément (aucun rendu React pendant le défilement).
 *
 * Renvoie une ref à poser sur l'élément à réduire.
 */
export default function useCompactOnScroll(enabled, resetKey) {
  const elRef = useRef(null);
  const target = useRef(0);
  const shown = useRef(0);
  const anim = useRef(0);

  // Rattrape la cible image par image, puis s'arrête.
  const animate = useCallback(() => {
    anim.current = 0;
    const gap = target.current - shown.current;
    shown.current = Math.abs(gap) < 0.002 || reducedMotion() ? target.current : shown.current + gap * EASE;
    if (elRef.current) paint(elRef.current, shown.current);
    if (shown.current !== target.current) anim.current = requestAnimationFrame(animate);
  }, []);

  const moveTo = useCallback((p) => {
    target.current = p;
    if (!anim.current) anim.current = requestAnimationFrame(animate);
  }, [animate]);

  const ref = useCallback((el) => {
    elRef.current = el;
    if (el) paint(el, shown.current);
  }, []);

  // Nouvel écran : la barre revient pleine taille.
  useEffect(() => { moveTo(0); }, [resetKey, moveTo]);

  // Démontage (ou double montage du StrictMode) : l'image en attente est
  // annulée ET oubliée, sinon plus aucune animation ne repartirait.
  useEffect(() => () => {
    if (anim.current) cancelAnimationFrame(anim.current);
    anim.current = 0;
  }, []);

  useEffect(() => {
    if (!enabled || typeof document === 'undefined') {
      moveTo(0);
      return undefined;
    }
    // Dernière position vue, par conteneur qui défile.
    const lastY = new WeakMap();
    let pending = null;
    let frame = 0;
    let settleTimer = 0;
    // Doigt posé : pas de calage (une pause en plein glissé ne doit pas la faire sauter).
    let touching = false;

    const settle = () => {
      if (touching) return;
      moveTo(target.current >= 0.5 ? 1 : 0);
    };

    const read = () => {
      frame = 0;
      const el = pending;
      pending = null;
      if (!el) return;
      const y = el.scrollTop;
      const prev = lastY.get(el);
      lastY.set(el, y);
      if (prev === undefined || y === prev) return; // première vue, ou horizontal
      let p = target.current + (y - prev) / RANGE;
      p = Math.min(1, Math.max(0, p));
      // Au haut de la page, la barre est forcément (presque) pleine taille.
      p = Math.min(p, Math.max(0, y) / TOP_ZONE);
      moveTo(p);
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, SETTLE_DELAY);
    };

    const onScroll = (e) => {
      const el = e.target === document ? document.scrollingElement : e.target;
      if (!el || typeof el.scrollTop !== 'number') return;
      pending = el;
      if (!frame) frame = requestAnimationFrame(read);
    };

    const onTouchStart = () => { touching = true; clearTimeout(settleTimer); };
    // Doigt levé : l'élan peut encore faire défiler (chaque défilement relance
    // l'attente) ; sinon la barre se cale.
    const onTouchEnd = () => {
      touching = false;
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, SETTLE_DELAY);
    };

    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    document.addEventListener('touchstart', onTouchStart, { capture: true, passive: true });
    document.addEventListener('touchend', onTouchEnd, { capture: true, passive: true });
    document.addEventListener('touchcancel', onTouchEnd, { capture: true, passive: true });
    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true });
      document.removeEventListener('touchstart', onTouchStart, { capture: true });
      document.removeEventListener('touchend', onTouchEnd, { capture: true });
      document.removeEventListener('touchcancel', onTouchEnd, { capture: true });
      if (frame) cancelAnimationFrame(frame);
      clearTimeout(settleTimer);
    };
  }, [enabled, moveTo]);

  return ref;
}
